import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { LogIn, Mail, ArrowLeft, ShieldCheck, Sun, Moon, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import GuidedDemoForm from '@/components/GuidedDemoForm';
import { BRAND, logoForTheme, APP_NAME } from '@/lib/brand';
const BG_IMAGES = [
  'https://images.unsplash.com/photo-1770777843445-2a1621b1201d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxzYWxlcyUyMHRlYW0lMjB0ZWNobm9sb2d5JTIwb2ZmaWNlJTIwY29sbGFib3JhdGlvbnxlbnwwfHx8fDE3NzYyMjA0MzJ8MA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1580983553600-c49a1d083f54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxzYWxlcyUyMHRlYW0lMjB0ZWNobm9sb2d5JTIwb2ZmaWNlJTIwY29sbGFib3JhdGlvbnxlbnwwfHx8fDE3NzYyMjA0MzJ8MA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1629904853716-f0bc54eea481?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxzYWxlcyUyMHRlYW0lMjB0ZWNobm9sb2d5JTIwb2ZmaWNlJTIwY29sbGFib3JhdGlvbnxlbnwwfHx8fDE3NzYyMjA0MzJ8MA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1580894899378-92e56886cd4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxzYWxlcyUyMHRlYW0lMjB0ZWNobm9sb2d5JTIwb2ZmaWNlJTIwY29sbGFib3JhdGlvbnxlbnwwfHx8fDE3NzYyMjA0MzJ8MA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1758599543114-248f7211fddd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxzbWFydCUyMGNpdHklMjBidXNpbmVzcyUyMHRlY2hub2xvZ3klMjB0ZWFtfGVufDB8fHx8MTc3NjIyMDQzOHww&ixlib=rb-4.1.0&q=85',
];

export default function LoginPage() {
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  // Cloudflare Turnstile. Sustituye al captcha casero, cuya unica defensa real
  // era "tardaste mas de 0,8 segundos": no mandar el campo bastaba para saltarlo.
  //   tsCfg    -> {enabled, site_key} que manda el servidor
  //   tsToken  -> lo que entrega el widget al confirmar que eres persona
  //   tsFallo  -> el widget no cargo (red que bloquea a Cloudflare) -> se deja entrar
  const [tsCfg, setTsCfg] = useState({ enabled: false, site_key: '' });
  const [tsToken, setTsToken] = useState('');
  const [tsFallo, setTsFallo] = useState(false);
  const tsBox = useRef(null);
  const tsId = useRef(null);
  const [bgImage, setBgImage] = useState(BG_IMAGES[0]);


  // Pide la llave publica. El dibujo del widget va en OTRO useEffect, cuando
  // el <div ref={tsBox}> ya existe (tsCfg.enabled === true). Si se renderiza
  // en el mismo tick que setTsCfg, tsBox.current es null y el captcha NUNCA
  // aparece (bug visto en prod/lab ago-2026).
  //
  // RESPALDO 8s: si Cloudflare no carga (red de oficina, adblock), se deja
  // entrar y el backend tolera falta de token salvo TURNSTILE_OBLIGATORIO.
  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const r = await api.get('/auth/turnstile');
        if (cancelado) return;
        const cfg = r.data || {};
        if (!cfg.enabled || !cfg.site_key) {
          setTsCfg({ enabled: false, site_key: '' });
          return;
        }
        setTsCfg({ enabled: true, site_key: cfg.site_key });
      } catch {
        if (!cancelado) setTsFallo(true);
      }
    })();
    setBgImage(BG_IMAGES[Math.floor(Math.random() * BG_IMAGES.length)]);
    return () => { cancelado = true; };
  }, []);

  useEffect(() => {
    if (!tsCfg.enabled || !tsCfg.site_key || tsFallo) return;

    let respaldo = setTimeout(() => {
      // Solo si el widget NUNCA se montó. Si ya está en pantalla esperando
      // el clic, no lo escondas (eso era el "No pudimos cargar..." a los 8s).
      if (tsId.current == null) {
        setTsFallo((ya) => {
          if (!ya) console.warn('[turnstile] el recuadro no cargo en 12s — se permite entrar');
          return true;
        });
      }
    }, 12000);

    const dibujar = () => {
      if (!window.turnstile || !tsBox.current) return;
      if (tsId.current !== null) {
        try { window.turnstile.remove(tsId.current); } catch (_) { /* ignore */ }
        tsId.current = null;
      }
      try {
        tsId.current = window.turnstile.render(tsBox.current, {
          sitekey: tsCfg.site_key,
          theme: 'dark',
          callback: (t) => { clearTimeout(respaldo); setTsToken(t); setTsFallo(false); },
          'expired-callback': () => setTsToken(''),
          'error-callback': () => {
            // Error de Cloudflare (dominio no autorizado, red). No tumbar
            // el recuadro si ya hay widget; el usuario aún puede resolverlo.
            if (tsId.current == null) {
              clearTimeout(respaldo);
              setTsFallo(true);
            }
          },
        });
        if (tsId.current != null) clearTimeout(respaldo);
      } catch (e) {
        console.warn('[turnstile] render failed', e);
        clearTimeout(respaldo);
        setTsFallo(true);
      }
    };

    if (window.turnstile) {
      // Esperar un frame para asegurar layout del div
      const raf = requestAnimationFrame(() => dibujar());
      return () => {
        cancelAnimationFrame(raf);
        clearTimeout(respaldo);
        if (window.turnstile && tsId.current !== null) {
          try { window.turnstile.remove(tsId.current); } catch (_) { /* ignore */ }
          tsId.current = null;
        }
      };
    }

    let s = document.getElementById('cf-turnstile-script');
    if (!s) {
      s = document.createElement('script');
      s.id = 'cf-turnstile-script';
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.defer = true;
      s.onerror = () => { clearTimeout(respaldo); setTsFallo(true); };
      document.head.appendChild(s);
    }
    const onLoad = () => dibujar();
    if (window.turnstile) dibujar();
    else s.addEventListener('load', onLoad);

    return () => {
      clearTimeout(respaldo);
      s.removeEventListener('load', onLoad);
      if (window.turnstile && tsId.current !== null) {
        try { window.turnstile.remove(tsId.current); } catch (_) { /* ignore */ }
        tsId.current = null;
      }
    };
  }, [tsCfg.enabled, tsCfg.site_key, tsFallo]);

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
      toast.success('Instrucciones enviadas al correo');
    } catch { toast.error('Error al enviar'); }
    finally { setLoading(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (tsCfg.enabled && !tsToken && !tsFallo) {
      toast.error('Confirma que no eres un robot');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        email: loginData.email,
        password: loginData.password,
      };
      if (tsToken) payload.turnstile_token = tsToken;
      await login(loginData.email, loginData.password, payload);
      toast.success(`Bienvenido a ${APP_NAME}`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar sesion');
      // El token es de un solo uso: tras un intento fallido hay que pedir otro,
      // o el siguiente seria rechazado por duplicado.
      setTsToken('');
      if (window.turnstile && tsId.current !== null) window.turnstile.reset(tsId.current);
    } finally { setLoading(false); }
  };

  const inputCls = "bg-white/[0.04] border-white/[0.06] focus:border-[#A5D62C]/40 focus:ring-[#A5D62C]/10 placeholder:text-white/20 h-11";

  return (
    <div className="min-h-screen flex bg-[#050507]" data-testid="login-page">
      {/* Left - Image Panel */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        <img
          src={bgImage}
          alt={`${APP_NAME} background dinámico`}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#050507] via-[#050507]/40 to-[#050507]/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050507] via-transparent to-[#050507]/30" />

        {/* Content over image */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 text-sm transition-colors w-fit" data-testid="login-back-home">
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>

          <div className="max-w-md">
            <img src={BRAND.logoWhite} alt="LEADCEM" className="h-24 mb-8" data-testid="login-logo-image" />
            <h2 className="font-display text-3xl font-bold leading-tight mb-4">
              Transforma tu operación comercial con <span className="text-[#A5D62C]">IA sincronizada</span>
            </h2>
            <p className="text-sm text-white/35 leading-relaxed mb-8">
              Activa una ejecución neurocomercial 24/7 para inmobiliario, desarrollos, brokers, automotriz y retail: más seguimiento, mejor timing y cierres más inteligentes.
            </p>
            <div className="flex items-center gap-6">
              <div>
                <p className="text-2xl font-bold text-[#A5D62C]">5+</p>
                <p className="text-[10px] text-white/30 uppercase tracking-wider">Segmentos</p>
              </div>
              <div className="w-px h-8 bg-white/[0.06]" />
              <div>
                <p className="text-2xl font-bold text-white/80">24/7</p>
                <p className="text-[10px] text-white/30 uppercase tracking-wider">IA activa</p>
              </div>
              <div className="w-px h-8 bg-white/[0.06]" />
              <div>
                <p className="text-2xl font-bold text-white/80">70%</p>
                <p className="text-[10px] text-white/30 uppercase tracking-wider">+Eficiencia</p>
              </div>
            </div>
          </div>

          <p className="text-[10px] text-white/15">&copy; {new Date().getFullYear()} {APP_NAME}. Sales Intelligence Platform.</p>
        </div>
      </div>

      {/* Right - Form Panel */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 relative">
        {/* Subtle orb */}
        <div className="absolute top-1/4 right-1/4 w-[300px] h-[300px] bg-[#A5D62C]/[0.02] rounded-full blur-[100px]" />

        <div className="w-full max-w-[420px] relative z-10">
          {/* Mobile logo */}
          <div className="lg:hidden mb-10">
            <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 text-sm transition-colors mb-6" data-testid="login-back-home-mobile">
              <ArrowLeft className="w-4 h-4" />
              Volver
            </Link>
            <img src={logoForTheme(theme)} alt="LEADCEM" className="h-20" data-testid="login-logo-mobile" />
          </div>

          <div className="mb-8">
            <h1 className="font-display text-2xl font-bold mb-2">Bienvenido de vuelta</h1>
            <p className="text-sm text-white/35">Ingresa a tu cuenta o solicita una visita guiada sin costo</p>
          </div>

          <div className="w-full bg-white/[0.03] border border-white/[0.04] rounded-xl p-5 mb-6" data-testid="login-card">
            <div className="mb-4 flex items-center gap-2 text-[#A5D62C] text-sm font-medium" data-testid="login-only-label">
              <LogIn className="w-4 h-4" />
              Acceso a cuenta existente
            </div>
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="login-email" className="text-white/60 text-xs font-medium">Correo electronico</Label>
                <Input
                  id="login-email"
                  data-testid="login-email"
                  type="email"
                  placeholder="tu@correo.com"
                  value={loginData.email}
                  onChange={e => setLoginData({...loginData, email: e.target.value})}
                  required
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password" className="text-white/60 text-xs font-medium">Contrasena</Label>
                <Input
                  id="login-password"
                  data-testid="login-password"
                  type="password"
                  placeholder="********"
                  value={loginData.password}
                  onChange={e => setLoginData({...loginData, password: e.target.value})}
                  required
                  className={inputCls}
                />
              </div>
              {tsCfg.enabled && !tsFallo && (
                <div className="space-y-2" data-testid="turnstile-section">
                  <div ref={tsBox} className="flex justify-center min-h-[65px] items-center" data-testid="turnstile-widget" />
                  {!tsToken && (
                    <p className="text-[10px] text-white/35" data-testid="login-turnstile-helper-text">
                      Confirmando que eres una persona...
                    </p>
                  )}
                </div>
              )}
              {tsCfg.enabled && tsFallo && (
                <div className="flex items-center gap-2 p-2 rounded-lg border border-white/[0.06] bg-white/[0.02]" data-testid="turnstile-fallback">
                  <ShieldCheck className="w-3.5 h-3.5 text-white/30 shrink-0" />
                  <p className="text-[10px] text-white/35">
                    No pudimos cargar la verificacion de seguridad. Puedes entrar igual.
                  </p>
                </div>
              )}
              <div className="flex justify-center">
                <button type="button" onClick={toggleTheme} className="flex items-center gap-1.5 text-[10px] text-white/30 hover:text-white/60 transition-colors" data-testid="login-theme-toggle">
                  {theme === 'dark' ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
                  {theme === 'dark' ? 'Modo Claro' : 'Modo Oscuro'}
                </button>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold h-11 rounded-lg shadow-[0_0_30px_rgba(165,214,44,0.1)] hover:shadow-[0_0_40px_rgba(165,214,44,0.2)] transition-all duration-300"
                data-testid="login-submit"
                disabled={loading || !!(tsCfg.enabled && !tsToken && !tsFallo)}
              >
                {loading ? 'Ingresando...' : (tsCfg.enabled && !tsToken && !tsFallo) ? 'Verificando seguridad...' : 'Iniciar Sesion'}
              </Button>
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="w-full text-center text-xs text-white/30 hover:text-[#A5D62C] transition-colors duration-200"
                data-testid="forgot-password-link"
              >
                Olvide mi contrasena
              </button>
            </form>
          </div>

          <div className="rounded-xl border border-cyan-300/20 bg-cyan-300/10 p-4" data-testid="login-guided-demo-block">
            <GuidedDemoForm
              origin="login_page"
              title="¿Aún no tienes acceso? Solicita visita guiada / demo sin costo"
            />
          </div>

          <div className="pt-4 text-center text-xs text-white/40">
            <Link
              to="/terminos"
              className="hover:text-white/70 transition-colors"
              data-testid="login-terminos-link"
            >
              Términos y Condiciones
            </Link>
            <span className="mx-2 text-white/20">·</span>
            <Link
              to="/privacidad"
              className="hover:text-white/70 transition-colors"
              data-testid="login-privacidad-link"
            >
              Aviso de Privacidad
            </Link>
          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onOpenChange={v => { setForgotOpen(v); if (!v) setForgotSent(false); }}>
        <DialogContent className="max-w-sm bg-[#0a0c12] border-white/[0.06]">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#A5D62C]" />Recuperar Contrasena
            </DialogTitle>
            <DialogDescription className="text-white/40">Ingresa tu correo para recibir instrucciones</DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <div className="text-center py-4">
              <p className="text-[#A5D62C] text-sm mb-2">Instrucciones enviadas</p>
              <p className="text-white/30 text-xs">Revisa tu bandeja de entrada</p>
            </div>
          ) : (
            <form onSubmit={handleForgot} className="space-y-4">
              <Input
                data-testid="forgot-email"
                type="email"
                value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
                placeholder="tu@correo.com"
                required
                className={inputCls}
              />
              <Button
                type="submit"
                className="w-full bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold"
                data-testid="forgot-submit"
                disabled={loading}
              >
                {loading ? 'Enviando...' : 'Enviar Instrucciones'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
